/**
 * RECIPIENT-REACH.UTILS
 * Contacts that make a recipient reachable with the diffusion modes of a project (same rule as the server, which counts the users)
 */

export type ReachContact = "email" | "phone" | "address"

//Contacts to look at, in the order of the modes. Empty when nobody can be unreachable : no mode chosen,
//or QR code letters without address on them (handed over)
export function getReachContacts(
  diffusionMode: { email?: boolean; sms?: boolean; qrcode?: boolean } | null | undefined,
  qrcodeOptions: { hideAddress?: boolean } | null | undefined
): ReachContact[] {
  const contacts: ReachContact[] = []
  if (diffusionMode?.email) contacts.push("email")
  if (diffusionMode?.sms) contacts.push("phone")
  if (diffusionMode?.qrcode) contacts.push("address")

  if (contacts.length === 0) return []
  if (diffusionMode?.qrcode && qrcodeOptions?.hideAddress) return []
  return contacts
}

//Names of some users to give an example : only those that have a name or an e-mail address (a list of empty names tells nothing).
//Empty text when there is none. "..." when there are more users than examples.
export function formatUserExamples(
  users: { username?: string | null; email?: string | null }[],
  count: number,
  format: (list: string) => string
): string {
  const names = users.map((user) => (user.username || user.email || "").trim()).filter((name) => name.length > 0)
  if (names.length === 0) return ""
  return format(names.join(", ") + (count > names.length ? "..." : ""))
}

//State of each contact of a recipient : present or not, and needed or not by the modes of the project (the list shows it at a glance).
//An address is needed by the QR code letters, unless they are handed over
export function getContactStates(
  user: { email?: string | null; phone?: string | null; address?: string | null },
  diffusionMode: { email?: boolean; sms?: boolean; qrcode?: boolean } | null | undefined,
  qrcodeOptions: { hideAddress?: boolean } | null | undefined
): { contact: ReachContact; present: boolean; required: boolean }[] {
  const filled = (value: string | null | undefined) => (value || "").trim().length > 0
  return [
    { contact: "email", present: filled(user.email), required: !!diffusionMode?.email },
    { contact: "phone", present: filled(user.phone), required: !!diffusionMode?.sms },
    { contact: "address", present: filled(user.address), required: !!diffusionMode?.qrcode && !qrcodeOptions?.hideAddress }
  ]
}
